import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import type { PaginatedUserList } from '../../../js/api';
import { SignaturesService, UserService } from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { reactSelectReset } from '../utils/utils';
import { Button } from './button';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from './dialog';
import { Field, Label } from './fieldset';

type UserOption = { value: string; label: string };

// Render the option menu above the dialog (react-select in a modal).
const selectStyles = {
  ...reactSelectReset,
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 70 }),
};

type RequestSignatureDialogProps = {
  open: boolean;
  /** SignatureRequest id the invited user will be asked to sign. */
  signatureRequestId: string | null;
  /** Org whose active users may be invited (the HCE participant's org). */
  healthcareEntityId: string;
  onClose: () => void;
  onSuccess: () => void;
};

function userLabel(user: { first_name?: string; last_name?: string; email: string }): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${user.email})` : user.email;
}

/**
 * Staff-only nudge: pick an active user of the signing organization and send
 * them a directed magic-link invitation to sign the document.
 */
export function RequestSignatureDialog({
  open,
  signatureRequestId,
  healthcareEntityId,
  onClose,
  onSuccess,
}: RequestSignatureDialogProps) {
  const [selectedEmail, setSelectedEmail] = useState('');

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['org-active-users', healthcareEntityId],
    queryFn: () => UserService.userList({ active: true, healthcareEntityId }),
    enabled: open && !!healthcareEntityId,
    select: (data: PaginatedUserList) => data?.results ?? [],
  });

  const userOptions = useMemo<UserOption[]>(
    () => (users ?? []).map((user) => ({ value: user.email, label: userLabel(user) })),
    [users]
  );
  const selectedOption = userOptions.find((o) => o.value === selectedEmail) ?? null;

  const inviteMutation = useMutation({
    mutationFn: (assignedUserEmail: string) =>
      SignaturesService.signaturesRequestsRequestSignatureCreate({
        id: signatureRequestId!,
        requestBody: { assigned_user_email: assignedUserEmail },
      }),
    onSuccess: () => {
      toast.success('Signature request sent');
      onSuccess();
      handleClose();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to send signature request', handle403: true });
    },
  });

  const handleClose = () => {
    setSelectedEmail('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={() => !inviteMutation.isPending && handleClose()} size="lg">
      <DialogTitle>Request signature</DialogTitle>
      <DialogDescription>
        Send an active user of the organization a secure link to review and sign this document.
      </DialogDescription>
      <DialogBody>
        <Field>
          <Label>Organization user</Label>
          <Select<UserOption>
            inputId="request-signature-user-select"
            options={userOptions}
            value={selectedOption}
            onChange={(option) => setSelectedEmail(option?.value ?? '')}
            isLoading={isLoadingUsers}
            isDisabled={inviteMutation.isPending}
            isClearable
            placeholder="Search users…"
            styles={selectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          />
        </Field>
      </DialogBody>
      <DialogActions>
        <Button outline type="button" onClick={handleClose} disabled={inviteMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          color="brandgreen0"
          onClick={() => selectedEmail && inviteMutation.mutate(selectedEmail)}
          disabled={!selectedEmail || inviteMutation.isPending}
          data-testid="request-signature-submit"
        >
          {inviteMutation.isPending ? 'Sending…' : 'Send request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
